import {
    isArray,
    isBoolean,
    isDate,
    isEmpty,
    isError,
    isNaN,
    isNil,
    isNull,
    isNumber,
    isObject,
    isRegExp,
    isString,
    isUndefined,
    omitBy,
    reject,
} from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import { COLUMN_TYPES } from '../../../data-table/DataTableColumns';
import { FORMATTER_TYPES, DEFAULT_INVALID_VALUE, decimalFormatter } from '../../../../utilities/FormattingUtilities';

import { VOIP_SIP_STATUS_CODES, VOIP_CODECS, VOIP_ERROR_CODES } from '../TestLogContants';
import { getMetricStatusRendererWithThresholdMetrics, getThresholds } from '../MetricStatus';
import { getThresholdsTooltip, getThresholdsKeys } from '../Thresholds';
import { semColumnRenderer, semColumnTooltip } from '../Sem';

export const LQMOS_RX = 'LqmosRx';
export const LQMOS_TX = 'LqmosTx';

export function getVoIPColumns(test) {
    const metricWidth = 90;
    const labels = {
        caller: t`Caller`,
        callee: t`Callee`,
        mos: t`MOS`,
        loss: t`Loss`,
        latency: t`Latency`,
        jitter: t`Jitter`,
        delay: t`Delay`,
    };
    const rxThresholds = getThresholdsKeys(LQMOS_RX, test);
    const txThresholds = getThresholdsKeys(LQMOS_TX, test);

    // TODO: Clean up this empty checking code.  I don't really understand what makes a metric 'empty'
    //       well enough to clean it up right now...
    function isMetricEmpty(value) {
        if (isMetricEmptyIgnoreNegative(value)) {
            return true;
        }

        const valueAsInt = parseInt(value, 10);
        if (valueAsInt === -1) {
            return true;
        }

        return false;
    }

    function isBlank(x) {
        if (isNumber(x)) return isNaN(x);
        if (isBoolean(x) || isDate(x) || isRegExp(x) || isError(x)) return false;

        let val;

        if (isString(x)) val = x.trim();
        if (isArray(x)) val = reject(x, (xx) => isUndefined(xx) || isNull(xx));
        if (isObject(x)) val = omitBy(x, (xx) => isUndefined(xx) || isNull(xx));

        return isEmpty(val);
    }

    function isMetricEmptyIgnoreNegative(value) {
        if (isBlank(value)) {
            return true;
        }

        if (value === 'NaN') {
            return true;
        }

        return false;
    }

    function formatVoipErrorCode(errorCode) {
        // MDL - Checking specifically for null and undefined because
        //  zero means we have a value in this case.
        if (!isNil(errorCode)) {
            const errorCodeStr = String(errorCode);
            const errorTranslation = VOIP_ERROR_CODES[errorCodeStr];

            if (errorTranslation) {
                return i18n._(errorTranslation);
            }

            return errorCodeStr;
        }

        return DEFAULT_INVALID_VALUE;
    }

    function formatMos(mos) {
        if (isMetricEmpty(mos)) return DEFAULT_INVALID_VALUE;
        return decimalFormatter(mos, { forceDecimals: true });
    }

    function formatSecurityMode(mode) {
        const modeAsInt = parseInt(mode, 10);
        if (modeAsInt === 2) return 'Yes';
        return 'No';
    }

    function formatVoipSIPStatus(sipStatus) {
        const empty = DEFAULT_INVALID_VALUE;
        let formatted = empty;

        // MDL - Checking specifically for null and undefined because
        //  zero means we have a value in this case.
        if (sipStatus !== null && sipStatus !== undefined) {
            // So we don't have issues looking up errors we have not translated.
            //  The reason I started to do this was because the pulse was sending up an error 1000
            //  which I'm pretty sure is not valid...
            formatted = i18n._(VOIP_SIP_STATUS_CODES?.[sipStatus.toString()] || empty);

            // If we have this error translated then continue formatting.
            if (formatted !== empty) {
                // Add the SIP Code so it looks like "408 - Request Timeout"
                formatted = `${sipStatus} - ${formatted}`;
            }
        }

        return formatted;
    }

    function formatCodec(metric) {
        const supported = [0, 2, 3, 8, 9, 18];

        // MDL - Checking specifically for null and undefined because
        //  zero means we have a value in this case.
        if (metric !== null && metric !== undefined) {
            // So we don't have issues looking up codecs we have not translated.
            if (supported.indexOf(metric) > -1) {
                return i18n._(VOIP_CODECS?.[metric.toString()]);
            }
        }

        return DEFAULT_INVALID_VALUE;
    }

    const getTooltipForMos = (thresholdMetrics, row) => {
        const thresholds = getThresholds(thresholdMetrics, row);
        if (!thresholds) {
            return '';
        }
        const degraded = formatMos(thresholds.degraded);
        const critical = formatMos(thresholds.critical);
        return getThresholdsTooltip(degraded, critical);
    };

    return [
        {
            width: 150,
            title: t`Result`,
            mapping: 'avgErrorCode',
            sortable: true,
            formatter: formatVoipErrorCode,
        },
        {
            id: 'SEM-message',
            width: 40,
            sortable: false,
            mapping: 'sem-capture-error',
            getTooltip: (row) => semColumnTooltip(row),
            renderer: semColumnRenderer,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            id: 'mos-caller',
            group: labels.mos,
            width: metricWidth,
            align: 'center',
            title: labels.caller,
            mapping: 'avgLqmosRx',
            sortable: true,
            formatter: formatMos,
            getTooltip: (row) => getTooltipForMos(rxThresholds, row),
            renderer: getMetricStatusRendererWithThresholdMetrics(test, rxThresholds),
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            id: 'mos-callee',
            group: labels.mos,
            width: metricWidth,
            align: 'center',
            title: labels.callee,
            mapping: 'avgLqmosTx',
            sortable: true,
            formatter: formatMos,
            getTooltip: (row) => getTooltipForMos(txThresholds, row),
            renderer: getMetricStatusRendererWithThresholdMetrics(test, txThresholds),
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            id: 'delay-dial',
            width: 100,
            group: labels.delay,
            sortable: true,
            tooltip: false,
            title: t`Dial`,
            mapping: 'avgDialDelay',
            formatter: FORMATTER_TYPES.DURATION,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            id: 'delay-ring',
            width: 100,
            group: labels.delay,
            sortable: true,
            title: t`Ring`,
            tooltip: false,
            mapping: 'avgRingDelay',
            formatter: FORMATTER_TYPES.DURATION,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            id: 'loss-caller',
            group: labels.loss,
            width: metricWidth,
            sortable: true,
            align: 'center',
            title: labels.caller,
            tooltip: false,
            mapping: 'avgPctLossRx',
            formatter: FORMATTER_TYPES.PERCENT,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            id: 'loss-callee',
            group: labels.loss,
            width: metricWidth,
            align: 'center',
            sortable: true,
            title: labels.callee,
            tooltip: false,
            mapping: 'avgPctLossTx',
            formatter: FORMATTER_TYPES.PERCENT,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            id: 'latency-caller',
            group: labels.latency,
            width: metricWidth,
            align: 'center',
            title: labels.caller,
            sortable: true,
            tooltip: false,
            mapping: 'avgRttRx',
            formatter: FORMATTER_TYPES.DURATION,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            id: 'latency-callee',
            group: labels.latency,
            width: metricWidth,
            align: 'center',
            title: labels.callee,
            sortable: true,
            mapping: 'avgRttTx',
            formatter: FORMATTER_TYPES.DURATION,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            id: 'jitter-caller',
            group: labels.jitter,
            width: metricWidth,
            align: 'center',
            title: labels.caller,
            sortable: true,
            tooltip: false,
            mapping: 'avgJitterRx',
            formatter: FORMATTER_TYPES.DURATION,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            id: 'jitter-callee',
            group: labels.jitter,
            width: metricWidth,
            align: 'center',
            title: labels.callee,
            sortable: true,
            tooltip: false,
            mapping: 'avgJitterTx',
            formatter: FORMATTER_TYPES.DURATION,
        },
        {
            width: 120,
            title: t`Secure`,
            mapping: 'avgSecurityMode',
            sortable: true,
            tooltip: false,
            formatter: formatSecurityMode,
        },
        {
            width: 200,
            title: t`SIP Status`,
            mapping: 'avgSipStatus',
            sortable: true,
            tooltip: false,
            formatter: formatVoipSIPStatus,
        },
        {
            width: 110,
            title: t`Codec`,
            mapping: 'avgCodecPt',
            sortable: true,
            tooltip: false,
            formatter: formatCodec,
        },
    ];
}
